import LZString from "lz-string";
import { useMemo } from "react";
import { createParam } from "solito";

import type { HealthRecordRefInput } from "@medbillai/generated-graphql-types";

import type { CaseType } from "@medbillai/schemas";
import { CASE_PREVIEW_ID } from "@medbillai/utils/urls";

import type { PreviewEventWithMetadata } from "../utils/types";

type Params = {
  // Can be undefined during initial hydration (cold start, deep link, background wake)
  id?: string;
  // Technically should be a boolean, but Solito gives us a string regardless.
  unread?: string;
  firstMessage?: string | undefined;
  title?: string;
  caseType?: CaseType;
  storeKey?: string;
  skipPageUILoad?: boolean;
  // Should only be passed if the CASE_SCREEN_REFACTOR flag is on
  config?: string;
  // An event whose flow opens on arrival, e.g. from an SMS request link.
  open?: string;
};

const { useParams } = createParam<Params>();

export const useCaseScreenParams = () => {
  const { params, setParams } = useParams();
  const decodedConfig = useMemo(
    () => (params.config ? decodePreviewConfig(params.config) : undefined),
    [params.config],
  );
  return {
    params: {
      ...params,
      unread: params.unread === "true",
      config: decodedConfig,
    },
    setParams,
  };
};

export const useIsPreviewCase = () => {
  const { params } = useCaseScreenParams();
  return params.id === CASE_PREVIEW_ID;
};

/** The health-data record a case is opened from. */
export type HealthRecordRef = HealthRecordRefInput;

export type PreviewConfig = {
  id: string;
  caseType: CaseType;
  title: string;
  events: PreviewEventWithMetadata[];
  healthRecord?: HealthRecordRef;
};

export const encodePreviewConfig = (config: PreviewConfig) => {
  return LZString.compressToEncodedURIComponent(JSON.stringify(config));
};

export const decodePreviewConfig = (encoded: string) => {
  try {
    return JSON.parse(
      LZString.decompressFromEncodedURIComponent(encoded),
    ) as PreviewConfig;
  } catch {
    return null;
  }
};
